import React from 'react';
import { DomainCheckoutData } from '../../pages/checkout/DomainCheckout';

interface DomainPlanStepProps {
  data: DomainCheckoutData;
  domainName: string;
  domainPrice: number;
  onNext: (data: Partial<DomainCheckoutData>) => void;
  onBack: () => void;
}

const DomainPlanStep: React.FC<DomainPlanStepProps> = ({ 
  data, 
  domainName, 
  domainPrice, 
  onNext, 
  onBack 
}) => {
  const handleNext = () => {
    // For domains, billing cycle is always yearly
    onNext({
      billingCycle: 'yearly',
      pricing: {
        domainPrice: domainPrice,
        discount: 0,
        total: domainPrice,
      },
    });
  };

  return (
    <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
      {/* Glossy overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
      
      <div className="max-w-2xl mx-auto relative z-10">
        {/* Badge */}
        <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full">
          <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
          <span className="text-sm font-semibold text-gray-700">Step 2: Review Domain</span>
        </div>
        
        <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6">
          Review Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Domain Registration</span>
        </h2>
      
      {/* Domain Details */}
      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Domain Registration</h3>
          <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
            Available
          </div>
        </div>
        
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Domain Name:</span>
            <span className="font-semibold text-gray-900">{domainName}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Registration Period:</span>
            <span className="font-semibold text-gray-900">1 Year</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Auto-Renewal:</span>
            <span className="font-semibold text-gray-900">Enabled</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Privacy Protection:</span>
            <span className="font-semibold text-gray-900">Included</span>
          </div>
        </div>
      </div>

      {/* Pricing Breakdown */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing Details</h3>
        
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Domain Registration (1 Year)</span>
            <span className="font-semibold text-gray-900">${domainPrice.toFixed(2)}</span>
          </div>
          
          <div className="flex justify-between items-center text-green-600">
            <span>Privacy Protection</span>
            <span className="font-semibold">FREE</span>
          </div>
          
          <div className="flex justify-between items-center text-green-600">
            <span>DNS Management</span>
            <span className="font-semibold">FREE</span>
          </div>
          
          <div className="flex justify-between items-center text-green-600">
            <span>Email Forwarding</span>
            <span className="font-semibold">FREE</span>
          </div>
          
          <hr className="my-3" />
          
          <div className="flex justify-between items-center text-lg font-bold">
            <span className="text-gray-900">Total (First Year)</span>
            <span className="text-primary-navy">${domainPrice.toFixed(2)}</span>
          </div>
          
          <div className="text-sm text-gray-500 mt-2">
            * Domain will auto-renew at ${domainPrice.toFixed(2)}/year unless cancelled
          </div>
        </div>
      </div>

      {/* Features Included */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">What's Included</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <div className="font-medium text-blue-900">Domain Privacy Protection</div>
              <div className="text-sm text-blue-700">Keep your personal information private</div>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <div className="font-medium text-blue-900">DNS Management</div>
              <div className="text-sm text-blue-700">Full control over your domain's DNS settings</div>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <div className="font-medium text-blue-900">Email Forwarding</div>
              <div className="text-sm text-blue-700">Forward emails to existing accounts</div>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <div className="font-medium text-blue-900">Auto-Renewal</div>
              <div className="text-sm text-blue-700">Never lose your domain accidentally</div>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <div className="font-medium text-blue-900">24/7 Support</div>
              <div className="text-sm text-blue-700">Expert help whenever you need it</div>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <div className="font-medium text-blue-900">Easy Management</div>
              <div className="text-sm text-blue-700">User-friendly control panel</div>
            </div>
          </div>
        </div>
      </div>

      {/* Important Notice */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <div className="flex">
          <svg className="w-5 h-5 text-yellow-400 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-yellow-800">Important Information</h4>
            <div className="text-sm text-yellow-700 mt-1">
              Domain registrations are non-refundable. Please ensure your domain name is spelled correctly before proceeding.
            </div>
          </div>
        </div>
      </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <button
            onClick={onBack}
            className="border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200 shadow-md"
          >
            Back to Search
          </button>
          
          <button
            onClick={handleNext}
            className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-3 rounded-lg font-semibold hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
            <span className="relative z-10">Continue to Payment</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DomainPlanStep;